import { supabase } from './supabase';

/**
 * Validate and clean a reservation URL
 */
export function cleanReservationUrl(url) {
  if (!url) return null;
  
  // Remove localhost prefixes that were accidentally added
  let cleaned = url.replace(/^https?:\/\/localhost:\d+\//, '');
  
  // Extract URL from text like "OHV Registration: http://..."
  const urlMatch = cleaned.match(/https?:\/\/[^\s]+/);
  if (urlMatch) {
    cleaned = urlMatch[0];
  }
  
  // Ensure URL starts with http/https
  if (!cleaned.startsWith('http://') && !cleaned.startsWith('https://')) {
    cleaned = 'https://' + cleaned;
  }
  
  // Remove URL encoding artifacts
  cleaned = cleaned.replace(/%20Registration:/, '');
  
  return cleaned;
}

/**
 * State bounding boxes (approximate)
 */
const STATE_BOUNDS = {
  'AL': { latMin: 30.1, latMax: 35.0, lngMin: -88.5, lngMax: -84.9 },
  'AK': { latMin: 51.3, latMax: 71.4, lngMin: -166.2, lngMax: -140.3 },
  'AZ': { latMin: 31.3, latMax: 37.0, lngMin: -114.8, lngMax: -109.0 },
  'AR': { latMin: 33.0, latMax: 36.5, lngMin: -94.4, lngMax: -89.6 },
  'CA': { latMin: 32.5, latMax: 42.0, lngMin: -124.4, lngMax: -114.1 },
  'CO': { latMin: 37.0, latMax: 41.0, lngMin: -109.0, lngMax: -102.0 },
  'CT': { latMin: 40.9, latMax: 42.1, lngMin: -73.7, lngMax: -71.8 },
  'DE': { latMin: 38.4, latMax: 39.8, lngMin: -75.8, lngMax: -75.0 },
  'FL': { latMin: 24.4, latMax: 30.9, lngMin: -87.6, lngMax: -80.0 },
  'GA': { latMin: 30.4, latMax: 35.0, lngMin: -85.6, lngMax: -80.8 },
  'HI': { latMin: 18.9, latMax: 22.2, lngMin: -160.2, lngMax: -154.8 },
  'ID': { latMin: 42.0, latMax: 49.0, lngMin: -117.2, lngMax: -111.0 },
  'IL': { latMin: 37.0, latMax: 42.5, lngMin: -91.5, lngMax: -87.0 },
  'IN': { latMin: 37.8, latMax: 41.8, lngMin: -88.1, lngMax: -84.8 },
  'IA': { latMin: 40.4, latMax: 43.5, lngMin: -96.6, lngMax: -90.1 },
  'KS': { latMin: 37.0, latMax: 40.0, lngMin: -102.1, lngMax: -94.3 },
  'KY': { latMin: 36.5, latMax: 39.1, lngMin: -89.6, lngMax: -81.9 },
  'LA': { latMin: 29.0, latMax: 33.0, lngMin: -94.0, lngMax: -88.8 },
  'ME': { latMin: 43.0, latMax: 47.5, lngMin: -71.1, lngMax: -66.9 },
  'MD': { latMin: 37.9, latMax: 39.7, lngMin: -79.5, lngMax: -75.0 },
  'MA': { latMin: 41.2, latMax: 42.9, lngMin: -73.5, lngMax: -69.9 },
  'MI': { latMin: 41.7, latMax: 48.3, lngMin: -90.4, lngMax: -83.3 },
  'MN': { latMin: 43.5, latMax: 49.4, lngMin: -97.2, lngMax: -89.5 },
  'MS': { latMin: 30.2, latMax: 34.9, lngMin: -91.7, lngMax: -88.1 },
  'MO': { latMin: 36.5, latMax: 40.6, lngMin: -95.8, lngMax: -89.1 },
  'MT': { latMin: 45.0, latMax: 49.0, lngMin: -116.0, lngMax: -104.0 },
  'NE': { latMin: 40.0, latMax: 43.0, lngMin: -104.1, lngMax: -95.3 },
  'NV': { latMin: 35.0, latMax: 42.0, lngMin: -120.0, lngMax: -114.4 },
  'NH': { latMin: 42.7, latMax: 45.3, lngMin: -72.6, lngMax: -70.7 },
  'NJ': { latMin: 38.9, latMax: 41.4, lngMin: -75.6, lngMax: -73.9 },
  'NM': { latMin: 31.8, latMax: 37.0, lngMin: -109.0, lngMax: -103.0 },
  'NY': { latMin: 40.5, latMax: 45.0, lngMin: -79.8, lngMax: -71.9 },
  'NC': { latMin: 33.8, latMax: 36.6, lngMin: -84.3, lngMax: -75.4 },
  'ND': { latMin: 46.3, latMax: 49.0, lngMin: -104.0, lngMax: -96.6 },
  'OH': { latMin: 38.4, latMax: 42.0, lngMin: -84.8, lngMax: -80.5 },
  'OK': { latMin: 33.6, latMax: 37.0, lngMin: -103.0, lngMax: -94.4 },
  'OR': { latMin: 42.0, latMax: 46.3, lngMin: -124.6, lngMax: -116.5 },
  'PA': { latMin: 39.7, latMax: 42.3, lngMin: -80.5, lngMax: -74.7 },
  'RI': { latMin: 41.1, latMax: 42.0, lngMin: -71.9, lngMax: -71.1 },
  'SC': { latMin: 32.0, latMax: 34.8, lngMin: -83.4, lngMax: -78.5 },
  'SD': { latMin: 42.5, latMax: 45.9, lngMin: -104.0, lngMax: -96.4 },
  'TN': { latMin: 35.0, latMax: 36.7, lngMin: -90.3, lngMax: -81.6 },
  'TX': { latMin: 25.8, latMax: 36.5, lngMin: -106.6, lngMax: -93.5 },
  'UT': { latMin: 37.0, latMax: 42.0, lngMin: -114.0, lngMax: -109.0 },
  'VT': { latMin: 42.7, latMax: 45.0, lngMin: -73.4, lngMax: -71.5 },
  'VA': { latMin: 36.5, latMax: 39.5, lngMin: -83.7, lngMax: -75.2 },
  'WA': { latMin: 45.6, latMax: 49.0, lngMin: -124.7, lngMax: -116.9 },
  'WV': { latMin: 37.2, latMax: 40.6, lngMin: -82.6, lngMax: -77.7 },
  'WI': { latMin: 42.5, latMax: 47.3, lngMin: -92.9, lngMax: -86.8 },
  'WY': { latMin: 41.0, latMax: 45.0, lngMin: -111.0, lngMax: -104.0 }
};

/**
 * Query facilities by state with reservation URLs
 * @param {string} stateCode - Two-letter state code
 * @param {number} offset - Pagination offset (default 0)
 * @param {number} limit - Results per page (default 1000)
 */
export async function getFacilitiesByState(stateCode, offset = 0, limit = 1000) {
  try {
    const bounds = STATE_BOUNDS[stateCode];
    if (!bounds) {
      throw new Error(`Invalid state code: ${stateCode}`);
    }

    const { data, error, count } = await supabase
      .from('facilities')
      .select(
        'id, FacilityID, FacilityName, FacilityTypeDescription, FacilityReservationURL, FacilityLatitude, FacilityLongitude, Keywords, FacilityDescription, FacilityDirections, FacilityPhone, FacilityUseFeeDescription, Reservable',
        { count: 'exact' }
      )
      .gte('FacilityLatitude', bounds.latMin)
      .lte('FacilityLatitude', bounds.latMax)
      .gte('FacilityLongitude', bounds.lngMin)
      .lte('FacilityLongitude', bounds.lngMax)
      .order('FacilityName')
      .range(offset, offset + limit - 1);

    if (error) {
      console.error(`Error fetching facilities for state ${stateCode}:`, error);
      throw error;
    }

    console.log(`Found ${data.length} facilities in ${stateCode} (total: ${count})`);
    
    return {
      all: data,
      withRecreationGov: data,
      total: count,
      offset: offset,
      limit: limit
    };
  } catch (error) {
    console.error(`Failed to query facilities for state ${stateCode}:`, error);
    throw error;
  }
}

/**
 * Query all facilities from Supabase with pagination (default search)
 * @param {number} offset - Pagination offset (default 0)
 * @param {number} limit - Results per page (default 1000)
 */
export async function getAllFacilities(offset = 0, limit = 1000) {
  try {
    const { data, error, count } = await supabase
      .from('facilities')
      .select(
        'id, FacilityID, FacilityName, FacilityTypeDescription, FacilityReservationURL, FacilityLatitude, FacilityLongitude, Keywords, FacilityDescription, FacilityDirections, FacilityPhone, FacilityUseFeeDescription, Reservable',
        { count: 'exact' }
      )
      .order('FacilityName')
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching all facilities:', error);
      throw error;
    }

    console.log(`Found ${data.length} facilities (total: ${count})`);
    
    return {
      all: data,
      total: count,
      offset: offset,
      limit: limit
    };
  } catch (error) {
    console.error('Failed to query all facilities:', error);
    throw error;
  }
}

/**
 * Get all unique facility types from Supabase
 */
export async function getAllFacilityTypes() {
  try {
    const { data, error } = await supabase
      .from('facilities')
      .select('FacilityTypeDescription')
      .not('FacilityTypeDescription', 'is', null);

    if (error) {
      console.error('Error fetching facility types:', error);
      throw error;
    }

    // Extract unique types and sort
    const uniqueTypes = Array.from(
      new Set(
        data
          .map(row => row.FacilityTypeDescription)
          .filter(Boolean)
      )
    ).sort();

    console.log(`Found ${uniqueTypes.length} unique facility types`);
    return uniqueTypes;
  } catch (error) {
    console.error('Failed to query facility types:', error);
    throw error;
  }
}

/**
 * Query facilities by type with pagination
 */
export async function getFacilitiesByType(facilityType, offset = 0, limit = 1000) {
  try {
    const { data, error, count } = await supabase
      .from('facilities')
      .select(
        'id, FacilityID, FacilityName, FacilityTypeDescription, FacilityReservationURL, FacilityLatitude, FacilityLongitude, Keywords, FacilityDescription, FacilityDirections, FacilityPhone, FacilityUseFeeDescription, Reservable',
        { count: 'exact' }
      )
      .eq('FacilityTypeDescription', facilityType)
      .order('FacilityName')
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching facilities by type:', error);
      throw error;
    }

    console.log(`Found ${data.length} facilities of type "${facilityType}" (total: ${count})`);
    
    return {
      all: data,
      total: count,
      offset: offset,
      limit: limit
    };
  } catch (error) {
    console.error(`Failed to query facilities by type "${facilityType}":`, error);
    throw error;
  }
}

/**
 * Query facilities by state AND type (combined filtering)
 * @param {string} stateCode - Two-letter state code
 * @param {string} facilityType - Facility type description
 * @param {number} offset - Pagination offset (default 0)
 * @param {number} limit - Results per page (default 1000)
 */
export async function getFacilitiesByStateAndType(stateCode, facilityType, offset = 0, limit = 1000) {
  try {
    const bounds = STATE_BOUNDS[stateCode];
    if (!bounds) {
      throw new Error(`Invalid state code: ${stateCode}`);
    }

    const { data, error, count } = await supabase
      .from('facilities')
      .select(
        'id, FacilityID, FacilityName, FacilityTypeDescription, FacilityReservationURL, FacilityLatitude, FacilityLongitude, Keywords, FacilityDescription, FacilityDirections, FacilityPhone, FacilityUseFeeDescription, Reservable',
        { count: 'exact' }
      )
      .gte('FacilityLatitude', bounds.latMin)
      .lte('FacilityLatitude', bounds.latMax)
      .gte('FacilityLongitude', bounds.lngMin)
      .lte('FacilityLongitude', bounds.lngMax)
      .eq('FacilityTypeDescription', facilityType)
      .order('FacilityName')
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching facilities by state and type:', error);
      throw error;
    }

    console.log(`Found ${data.length} ${facilityType} facilities in ${stateCode} (total: ${count})`);
    
    return {
      all: data,
      total: count,
      offset: offset,
      limit: limit
    };
  } catch (error) {
    console.error(`Failed to query ${stateCode} facilities by type "${facilityType}":`, error);
    throw error;
  }
}

/**
 * Search facilities by name or keywords (full text search)
 * @param {string} searchTerm - Search term
 * @param {number} offset - Pagination offset (default 0)
 * @param {number} limit - Results per page (default 1000)
 */
export async function searchFacilities(searchTerm, offset = 0, limit = 1000) {
  try {
    const searchLower = searchTerm.toLowerCase();
    
    const { data, error, count } = await supabase
      .from('facilities')
      .select(
        'id, FacilityID, FacilityName, FacilityTypeDescription, FacilityReservationURL, FacilityLatitude, FacilityLongitude, Keywords, FacilityDescription, FacilityDirections, FacilityPhone, FacilityUseFeeDescription, Reservable',
        { count: 'exact' }
      )
      .or(`FacilityName.ilike.%${searchLower}%,Keywords.ilike.%${searchLower}%`)
      .order('FacilityName')
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error searching facilities:', error);
      throw error;
    }

    console.log(`Found ${data.length} facilities matching "${searchTerm}" (total: ${count})`);
    
    return {
      all: data,
      total: count,
      offset: offset,
      limit: limit
    };
  } catch (error) {
    console.error(`Failed to search facilities for "${searchTerm}":`, error);
    throw error;
  }
}


/**
 * Query facilities in a geographic area with reservation URLs
 */
export async function getFacilitiesByArea(name, latMin, latMax, lngMin, lngMax) {
  try {
    const { data, error } = await supabase
      .from('facilities')
      .select(
        'id, FacilityID, FacilityName, FacilityTypeDescription, FacilityReservationURL, FacilityLatitude, FacilityLongitude, Keywords, Reservable'
      )
      .gte('FacilityLatitude', latMin)
      .lte('FacilityLatitude', latMax)
      .gte('FacilityLongitude', lngMin)
      .lte('FacilityLongitude', lngMax)
      .not('FacilityReservationURL', 'is', null)
      .order('FacilityName');

    if (error) {
      console.error(`Error fetching ${name} facilities:`, error);
      throw error;
    }

    console.log(`Found ${data.length} facilities in ${name} with URLs`);
    
    return {
      all: data,
      withRecreationGov: data,
    };
  } catch (error) {
    console.error(`Failed to query ${name} facilities:`, error);
    throw error;
  }
}

/**
 * Query Moab area for reservable facilities (any booking system)
 */
export async function getMoabFacilities() {
  return getFacilitiesByArea('Moab', 38.4, 38.7, -109.8, -109.4);
}

/**
 * Extract Recreation.gov facility ID from URL
 * e.g., https://www.recreation.gov/camping/campgrounds/232447 → 232447
 */
export function extractRecreationGovId(url) {
  if (!url) return null;
  const match = url.match(/campgrounds\/(\d+)/);
  return match ? match[1] : null;
}

/**
 * Search Recreation.gov for a facility by name
 */
export async function searchRecreationGovByName(facilityName) {
  try {
    console.log(`Searching Recreation.gov for: ${facilityName}`);
    
    // Query Recreation.gov's organization API to search for facilities
    const response = await fetch(
      `https://www.recreation.gov/api/camps/suggest?q=${encodeURIComponent(facilityName)}`,
      {
        method: 'GET',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          'Accept': 'application/json',
          'Accept-Language': 'en-US,en;q=0.9',
        }
      }
    );

    if (!response.ok) {
      console.warn(`Recreation.gov search error: ${response.status}`);
      throw new Error(`Recreation.gov search error: ${response.status}`);
    }

    const data = await response.json();
    console.log(`Found ${data.results?.length || 0} search results for "${facilityName}"`);
    
    // Filter for campground results
    const campgrounds = data.results?.filter(r => r.type === 'campground') || [];
    return campgrounds;
  } catch (error) {
    console.error('Failed to search Recreation.gov:', error);
    return [];
  }
}

/**
 * Fetch campsites for a specific Recreation.gov facility
 */
export async function getRecreationGovCampsites(facilityId) {
  try {
    console.log(`Fetching campsites for Recreation.gov facility: ${facilityId}`);
    
    const response = await fetch(
      `https://www.recreation.gov/api/camps/availability/campground/${facilityId}/month`,
      {
        method: 'GET',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          'Accept': 'application/json',
          'Accept-Language': 'en-US,en;q=0.9',
        }
      }
    );

    if (!response.ok) {
      console.warn(`Recreation.gov API error: ${response.status} for facility ${facilityId}`);
      throw new Error(`Recreation.gov API error: ${response.status}`);
    }

    const data = await response.json();
    
    console.log(`Found ${Object.keys(data.campsites).length} campsites`);
    
    return data;
  } catch (error) {
    console.error('Failed to fetch Recreation.gov campsites:', error);
    throw error;
  }
}

/**
 * Validate if a facility ID works with Recreation.gov API
 */
export async function validateRecreationGovId(facilityId) {
  try {
    console.log(`Validating facility ID: ${facilityId}`);
    
    const response = await fetch(
      `https://www.recreation.gov/api/camps/availability/campground/${facilityId}/month`,
      {
        method: 'GET',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          'Accept': 'application/json',
          'Accept-Language': 'en-US,en;q=0.9',
        }
      }
    );

    return response.ok;
  } catch (error) {
    return false;
  }
}

/**
 * Check if a campsite has availability on a specific date
 */
export function hasAvailability(campsitesStatus, campsiteId, dateString) {
  return campsitesStatus?.[campsiteId]?.[dateString] === 'Available';
}

/**
 * Get all available dates for a campsite
 */
export function getAvailableDates(campsitesStatus, campsiteId) {
  const status = campsitesStatus?.[campsiteId];
  if (!status) return [];
  
  return Object.entries(status)
    .filter(([date, availability]) => availability === 'Available')
    .map(([date]) => date)
    .sort();
}

/**
 * Get availability summary for a campsite
 */
export function getAvailabilitySummary(campsitesStatus, campsiteId) {
  const status = campsitesStatus?.[campsiteId];
  if (!status) return { available: 0, reserved: 0, total: 0, percentAvailable: 0 };
  
  const available = Object.values(status).filter(s => s === 'Available').length;
  const reserved = Object.values(status).filter(s => s === 'Reserved').length;
  
  return { 
    available, 
    reserved, 
    total: Object.keys(status).length,
    percentAvailable: Math.round((available / Object.keys(status).length) * 100)
  };
}

/**
 * Diagnostic: Check what data we actually have in the database
 */
export async function getDiagnosticInfo() {
  try {
    // Check a few random facilities
    const { data: sample, error: sampleError } = await supabase
      .from('facilities')
      .select('FacilityID, FacilityName, FacilityReservationURL, Reservable')
      .limit(5);

    // Count facilities with URLs
    const { data: withUrls, error: urlError } = await supabase
      .from('facilities')
      .select('FacilityID', { count: 'exact' })
      .not('FacilityReservationURL', 'is', null);

    // Count total facilities
    const { data: all, error: allError } = await supabase
      .from('facilities')
      .select('FacilityID', { count: 'exact' });

    return {
      sample: sample || [],
      facilitiesWithUrls: withUrls?.length || 0,
      totalFacilities: all?.length || 0,
      errors: { sampleError, urlError, allError }
    };
  } catch (error) {
    console.error('Diagnostic error:', error);
    return { error: error.message };
  }
}

/**
 * Get all campgrounds (filter by type) nationwide
 */
export async function getAllCampgroundsWithUrls(limit = 30) {
  try {
    const { data, error } = await supabase
      .from('facilities')
      .select(
        'id, FacilityID, FacilityName, FacilityTypeDescription, FacilityReservationURL, FacilityLatitude, FacilityLongitude, Keywords, Reservable'
      )
      .ilike('FacilityTypeDescription', '%campground%')
      .not('FacilityReservationURL', 'is', null)
      .limit(limit);

    if (error) {
      console.error('Error fetching campgrounds:', error);
      throw error;
    }

    console.log(`Found ${data.length} campgrounds with URLs`);
    
    const withIds = data.map(facility => ({
      ...facility,
      recreationGovId: extractRecreationGovId(facility.FacilityReservationURL),
    })).filter(f => f.recreationGovId);

    return withIds;
  } catch (error) {
    console.error('Failed to fetch campgrounds:', error);
    throw error;
  }
}
